import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { TextDiv } from "../common/Text"
import { useFormContext } from "react-hook-form"

interface DropdownPropsType {
  handleName: string
  selectOptions: string[]
  onChange: (...event: any[]) => void
  mode?: "left" | "right"
  $isError: boolean
}

const DropdownInput = ({
  handleName,
  selectOptions,
  onChange,
  mode,
  $isError,
}: DropdownPropsType) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { watch } = useFormContext()
  const isPhoneNumberVerified = watch("isPhoneNumberVerified")
  let selectedOption
  switch (handleName) {
    case "통신사":
      selectedOption = watch("phoneNumber.mobileCarrier")
      break
    case "직책":
      selectedOption = watch("position")
      break
    case "알게 된 경로":
      selectedOption = watch("advertisement")
      break
    case "학년":
      selectedOption = watch("schoolInfo.grade")
      break
    default:
      break
  }

  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (e: { target: any }) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        dropdownMenuRef.current?.classList.toggle("open")
        setTimeout(() => setIsOpen(false), 100)
      }
    }

    document.addEventListener("click", handleOutsideClick)

    return () => {
      document.removeEventListener("click", handleOutsideClick)
    }
  })

  const dropdownMenuRef = useRef<HTMLDivElement>(null)

  const handleClickDropdown = () => {
    if (isOpen) {
      dropdownMenuRef.current?.classList.toggle("open")
      setTimeout(() => setIsOpen(!isOpen), 100)
    } else {
      setIsOpen(!isOpen)
      setTimeout(() => dropdownMenuRef.current?.classList.toggle("open"), 100)
    }
  }

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownDiv
        ref={dropdownMenuRef}
        $items={selectOptions.length}
        mode={mode}
        $isError={$isError}
        className={
          handleName === "통신사" && isPhoneNumberVerified ? "disabled" : ""
        }
      >
        <DropdownHandle
          onClick={handleClickDropdown}
          className={
            handleName === "통신사" && isPhoneNumberVerified ? "disabled" : ""
          }
        >
          <TextDiv fontSize={16}>
            {selectedOption ? selectedOption : handleName}
          </TextDiv>
        </DropdownHandle>
        <DropdownMenu $items={selectOptions.length}>
          {selectOptions.map((option, index) => {
            return (
              <DropdownItem
                key={`${handleName}-${index}-${option}`}
                onClick={() => {
                  onChange(option)
                  handleClickDropdown()
                }}
              >
                {option}
              </DropdownItem>
            )
          })}
        </DropdownMenu>
      </DropdownDiv>
    </DropdownContainer>
  )
}

export default DropdownInput

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  height: 45px;
  min-width: 90px;
`

const DropdownDiv = styled.div<{
  $items: number
  mode?: string
  $isError: boolean
}>`
  width: 100%;
  position: absolute;
  z-index: 0;
  top: 0;
  height: 45px;
  box-sizing: border-box;
  border-width: 2px;
  border-style: solid;
  border-color: ${(props) =>
    props.$isError ? "var(--custom-light-red)" : "var(--custom-platinum)"};
  border-radius: ${(props) =>
    props.mode === "left"
      ? "20px 0px 0px 20px"
      : props.mode
      ? "0px 20px 20px 0px"
      : "20px"};
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &.open {
    height: ${(props) =>
      props.$items <= 4
        ? `calc(${props.$items} * 32px + 58px)`
        : `calc(4 * 32px + 58px)`};
    border-color: var(--custom-blue);
    z-index: 100;
  }

  &.disabled {
    pointer-events: none;
  }
`

const DropdownHandle = styled.div`
  height: 42px;
  width: 100%;
  background-color: var(--custom-white-smoke);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;

  &.disabled {
    background-color: var(--custom-platinum);
  }
`

const DropdownMenu = styled.div<{ $items: number }>`
  padding: 5px 0px;
  background-color: white;
  width: 100%;
  height: ${(props) =>
    props.$items <= 4
      ? `calc(${props.$items} * 32px + 10px)`
      : `calc(4 * 32px + 10px)`};
  border-radius: 0px 0px 20px 20px;
  border-top: none;
  overflow-y: scroll;
  overflow-x: hidden;
  transition: all 0.3s ease;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--custom-beige);
    width: 6px;
    border-radius: 10px;
    background-clip: padding-box;
    border: 2px solid transparent;
  }

  &::-webkit-scrollbar-button:vertical:end:decrement {
    display: block;
    height: 20px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 10px;
  }
`

const DropdownItem = styled.option`
  padding: 4px 15px;
  font-size: 16px;
  line-height: 24px;
  background-color: transparent;

  &:hover {
    background-color: var(--custom-white-smoke);
  }

  &:active {
    background-color: var(--custom-platinum);
  }
`
